import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { isFreeAdmin } from '../../lib/freeAdmins';
import type { SeasonData, Role } from '../../types/scoreboard';

interface ScoreTableProps {
  data: SeasonData;
}

type ViewMode = 'compact' | 'games' | 'full';

const ROLE_LABEL: Record<Role, string> = {
  мирный: 'Мир',
  мафия: 'Маф',
  дон: 'Дон',
  шериф: 'Шер',
};

const BLACK_ROLES = new Set<Role>(['мафия', 'дон']);

function formatGameHeader(gameDate: string, gameNumber: string): string {
  const parts = gameDate.split('-');
  if (parts.length !== 3) return `${gameDate} - ${gameNumber}`;
  const [, month, day] = parts;
  return `${day}/${month} - ${gameNumber}`;
}

export function ScoreTable({ data }: ScoreTableProps) {
  const { t } = useTranslation('scoreboard');
  const { user } = useAuthStore();
  const canEdit = isFreeAdmin(user?.email);
  const { scores, games } = data;

  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'compact' : 'games'
  );

  const sorted = [...scores].sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    const nameA = a.currentDisplayName ?? a.displayName;
    const nameB = b.currentDisplayName ?? b.displayName;
    return nameA.localeCompare(nameB, 'ru');
  });

  // Unique sorted dates for "По дням" view
  const uniqueDates = [...new Set(games.map((g) => g.gameDate))].sort();

  function dayTotal(playerKey: string, date: string): number | null {
    let sum = 0;
    let played = false;
    for (const g of games) {
      if (g.gameDate !== date) continue;
      const entry = g.players[playerKey];
      if (entry) { sum += entry.points; played = true; }
    }
    return played ? Math.round(sum * 10) / 10 : null;
  }

  function formatDateHeader(isoDate: string): string {
    const parts = isoDate.split('-');
    if (parts.length !== 3) return isoDate;
    const [, month, day] = parts;
    return `${day}/${month}`;
  }

  return (
    <div>
      <div className="flex justify-end gap-1 px-4 py-2 border-b border-gray-800">
        {(['compact', 'games', 'full'] as ViewMode[]).map((mode) => (
          <button
            key={mode}
            type="button"
            onClick={() => setViewMode(mode)}
            className={`px-3 py-1 text-xs font-mono transition-colors border ${
              viewMode === mode
                ? 'text-mafia-gold border-mafia-gold/50'
                : 'text-mafia-muted hover:text-mafia-paper border-transparent'
            }`}
          >
            {mode === 'compact' ? 'Краткий' : mode === 'games' ? 'По дням' : 'Детали'}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left py-3 px-4 text-gray-400 font-medium">#</th>
              <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('table.player')}</th>

              {viewMode === 'games' && uniqueDates.map((date) => (
                <th key={date} className="text-center py-3 px-3 text-gray-400 font-medium whitespace-nowrap">
                  {formatDateHeader(date)}
                </th>
              ))}

              {viewMode === 'full' && games.map((g) => (
                <th key={g.gameId} className="text-center py-3 px-3 text-gray-400 font-medium whitespace-nowrap">
                  {canEdit ? (
                    <Link
                      to={`/admin/scoreboard/edit/${g.seasonId}/${g.gameId}`}
                      className="hover:text-mafia-gold underline-offset-2 hover:underline transition-colors"
                    >
                      {formatGameHeader(g.gameDate, g.gameNumber)}
                    </Link>
                  ) : (
                    formatGameHeader(g.gameDate, g.gameNumber)
                  )}
                </th>
              ))}

              <th className="text-right py-3 px-4 text-gray-400 font-medium">{t('table.total')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((player, idx) => (
              <tr
                key={player.userId}
                className={`border-b border-gray-800 hover:bg-mafia-card/50 transition-colors ${
                  idx === 0 ? 'text-mafia-gold' : idx === 1 ? 'text-gray-300' : idx === 2 ? 'text-amber-600' : ''
                }`}
              >
                <td className="py-3 px-4 font-bold">{idx + 1}</td>
                <td className="py-3 px-4 font-medium">
                  {player.currentDisplayName ?? player.displayName}
                  {player.currentDisplayName && (
                    <span className="ml-2 text-xs text-mafia-muted font-mono font-normal">
                      (aka {player.displayName})
                    </span>
                  )}
                </td>

                {viewMode === 'games' && uniqueDates.map((date) => {
                  const pts = dayTotal(player.userId, date);
                  return (
                    <td key={date} className="py-3 px-3 text-center font-semibold">
                      {pts !== null ? pts.toFixed(1) : <span className="text-gray-600">—</span>}
                    </td>
                  );
                })}

                {viewMode === 'full' && games.map((g) => {
                  const entry = g.players[player.userId];
                  return (
                    <td key={g.gameId} className="py-3 px-3 text-center">
                      {entry ? (
                        <div>
                          <div className={`text-xs ${BLACK_ROLES.has(entry.role) ? 'text-mafia-crimson-light' : 'text-mafia-paper-dim'}`}>
                            {ROLE_LABEL[entry.role]}
                          </div>
                          <div className="font-semibold">{entry.points.toFixed(1)}</div>
                        </div>
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                    </td>
                  );
                })}

                <td className="py-3 px-4 text-right font-bold">{player.points.toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
